import React from 'react';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import type { AddOnSelectorProps, AddOnScheduleData } from '@/features/booking/types/ticketBookingTypes';


const AddOnSelector: React.FC<AddOnSelectorProps & { currency?: string }> = ({
  addOns,
  selectedAddOns,
  onAddOnToggle,
  onAddOnClick,
  guestCount,
  currency,
}) => {
  // Format price using currency if provided, otherwise show raw amount
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  const handleCardClick = (addOnSchedule: AddOnScheduleData) => {
    onAddOnClick(addOnSchedule);
  };

  const isAddOnSelected = (addOnId: string) => {
    return selectedAddOns[addOnId] > 0;
  };

  if (addOns.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">Add-ons</h2>
        <p className="text-gray-400">No add-ons available for this event.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">Add-ons</h2>
      
      {/* Add-ons Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-6 w-full">
        {addOns.map((addOnSchedule) => {
          const addOn = addOnSchedule.add_on;
          const isSelected = isAddOnSelected(addOn.id);
          const isUnlimited = addOnSchedule.remaining_inventory === null;
          const isInventoryLimited = !isUnlimited && (addOnSchedule.remaining_inventory as number) < guestCount && (addOnSchedule.remaining_inventory as number) > 0;
          
          const hasValidImage = addOn.image_id && addOn.image_id !== '00000000-0000-0000-0000-000000000000';
          const imageUrl = hasValidImage ? getSignedMediaUrl(addOn.image_id, 'image').url : null;

          return (
            <div 
              key={addOn.id}
              className="relative h-48 rounded-2xl overflow-hidden group cursor-pointer"
              onClick={() => handleCardClick(addOnSchedule)}
            >
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                style={{
                  backgroundImage: imageUrl 
                    ? `url(${imageUrl})` 
                    : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
                }}
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/40 transition-opacity duration-300 group-hover:bg-black/50" />
              
              {/* Content */}
              <div className="relative h-full p-6 flex flex-col justify-between">
                {/* Toggle Switch */}
                <div className="flex justify-end">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (guestCount > 0) {
                        onAddOnToggle(addOn.id, !isSelected);
                      }
                    }}
                    disabled={guestCount === 0}
                    className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                      guestCount === 0
                        ? 'bg-gray-600 cursor-not-allowed opacity-50'
                        : isSelected 
                        ? 'bg-blue-600' 
                        : 'bg-white/30'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                        isSelected ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Add-on Details */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">{addOn.name}</h3>
                    {isInventoryLimited && (
                      <span className="text-xs text-yellow-400 bg-yellow-900/20 px-2 py-1 rounded">
                        Limited
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-200 line-clamp-2">{addOn.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-blue-400">
                      {formatPrice(addOnSchedule.price)}
                    </div>
                    {!isUnlimited && (addOnSchedule.remaining_inventory as number) < 10 && (
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-red-400 bg-red-900/20 px-2 py-1 rounded">
                        <svg 
                          className="w-3.5 h-3.5" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12,6 12,12 16,14" />
                        </svg>
                        Only {addOnSchedule.remaining_inventory} left
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AddOnSelector;
